import { create } from 'zustand';
import type { Chat, Message } from '../lib/types';
import { chatApi, geminiApi } from '../lib/api';

interface ChatState {
    // Chat list
    chats: Chat[];
    isLoadingChats: boolean;

    // Current chat
    currentChatId: number | null;
    messages: Message[];
    isLoadingMessages: boolean;

    // Streaming state
    isStreaming: boolean;
    streamingContent: string;

    // Search
    searchQuery: string;

    // Selected model
    selectedModel: string;
    availableModels: { model_id: string; display_name: string }[];

    // Selected prompt
    selectedPromptId: number | null;

    // Actions
    loadChats: (search?: string) => Promise<void>;
    loadChat: (id: number, onNotFound?: () => void) => Promise<void>;
    createChat: (title?: string) => Promise<number | null>;
    deleteChat: (id: number) => Promise<void>;
    setCurrentChat: (id: number | null) => void;
    setSearchQuery: (query: string) => void;

    // Messaging
    sendMessage: (content: string, attachments?: File[], onNewChat?: (chatId: number) => void) => Promise<void>;
    clearStreaming: () => void;
    deleteMessages: (messageIds: number[]) => Promise<void>;

    // Models
    loadModels: () => Promise<void>;
    setSelectedModel: (model: string) => void;
    setSelectedPromptId: (id: number | null) => void;

    // Local message management
    addLocalMessage: (message: Omit<Message, 'id'>) => void;
}

export const useChatStore = create<ChatState>((set, get) => ({
    chats: [],
    isLoadingChats: false,
    currentChatId: null,
    messages: [],
    isLoadingMessages: false,
    isStreaming: false,
    streamingContent: '',
    searchQuery: '',
    selectedModel: localStorage.getItem('selectedModel') || 'gemini-2.0-flash',
    availableModels: [],
    selectedPromptId: null,

    loadChats: async (search?: string) => {
        set({ isLoadingChats: true });
        try {
            const response = await chatApi.list(search);
            if (response.success) {
                set({ chats: response.chats });
            }
        } catch (error) {
            console.error('Failed to load chats:', error);
        } finally {
            set({ isLoadingChats: false });
        }
    },

    loadChat: async (id: number, onNotFound?: () => void) => {
        set({ isLoadingMessages: true, currentChatId: id });
        try {
            const response = await chatApi.get(id);
            if (response.success) {
                set({
                    messages: response.chat.messages,
                    // Apply chat's saved model if available
                    selectedModel: response.chat.model || get().selectedModel,
                });
            } else {
                // Chat not found or access denied
                set({ currentChatId: null, messages: [] });
                if (onNotFound) onNotFound();
            }
        } catch (error: unknown) {
            console.error('Failed to load chat:', error);
            // Handle 404 specifically
            if (error instanceof Error && error.message.includes('not found')) {
                set({ currentChatId: null, messages: [] });
                if (onNotFound) onNotFound();
            }
        } finally {
            set({ isLoadingMessages: false });
        }
    },

    createChat: async (title?: string) => {
        try {
            // Use timestamp as default title if none provided
            if (!title) {
                const now = new Date();
                title = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
            }
            const response = await chatApi.create(title);
            if (response.success && response.chat) {
                const newChat = response.chat;
                set((state) => ({
                    chats: [newChat, ...state.chats],
                    currentChatId: newChat.id,
                    messages: [],
                }));
                return newChat.id;
            }
        } catch (error) {
            console.error('Failed to create chat:', error);
        }
        return null;
    },

    deleteChat: async (id: number) => {
        try {
            await chatApi.delete(id);
            set((state) => ({
                chats: state.chats.filter((c) => c.id !== id),
                currentChatId: state.currentChatId === id ? null : state.currentChatId,
                messages: state.currentChatId === id ? [] : state.messages,
            }));
        } catch (error) {
            console.error('Failed to delete chat:', error);
        }
    },

    setCurrentChat: (id: number | null) => {
        set({ currentChatId: id, messages: [] });
        if (id) {
            get().loadChat(id);
        }
    },

    setSearchQuery: (query: string) => {
        set({ searchQuery: query });
        get().loadChats(query || undefined);
    },

    sendMessage: async (content: string, attachments?: File[], onNewChat?: (chatId: number) => void) => {
        const { currentChatId, selectedModel, selectedPromptId } = get();

        // Add user message locally
        const userMessage: Omit<Message, 'id'> = {
            role: 'user',
            content,
            type: 'text',
            model: null,
            r2_path: null,
            created_at: new Date().toISOString(),
        };

        set((state) => ({
            messages: [...state.messages, { ...userMessage, id: Date.now() } as Message],
            isStreaming: true,
            streamingContent: '',
        }));

        // Prepare attachments
        let attachmentData: { type: string; data: string }[] | undefined;
        if (attachments && attachments.length > 0) {
            attachmentData = await Promise.all(
                attachments.map(async (file) => {
                    const reader = new FileReader();
                    const data = await new Promise<string>((resolve) => {
                        reader.onload = () => {
                            const result = reader.result as string;
                            resolve(result.split(',')[1]);
                        };
                        reader.readAsDataURL(file);
                    });
                    return { type: file.type, data };
                })
            );
        }

        try {
            await geminiApi.sendMessage(
                content,
                currentChatId || undefined,
                selectedModel,
                selectedPromptId || undefined,
                attachmentData,
                (event) => {
                    if (event.type === 'chat_id' && event.chatId) {
                        // New chat was created - navigate to it
                        if (!currentChatId) {
                            set({ currentChatId: event.chatId });
                            get().loadChats();
                            // Call navigation callback
                            if (onNewChat) {
                                onNewChat(event.chatId);
                            }
                        }
                    } else if (event.type === 'text' && event.content) {
                        set((state) => ({
                            streamingContent: state.streamingContent + event.content,
                        }));
                    } else if (event.type === 'image' && event.r2_path) {
                        // Image generated and saved to R2 - add as message with image
                        const imageMessage: Message = {
                            id: Date.now() + 1,
                            role: 'assistant',
                            content: get().streamingContent || '[Generated Image]',
                            type: 'image',
                            model: null,
                            r2_path: event.r2_path,
                            created_at: new Date().toISOString(),
                        };
                        set((state) => ({
                            messages: [...state.messages, imageMessage],
                            streamingContent: '',
                        }));
                    } else if (event.type === 'image_inline' && event.data) {
                        // Image generated but no R2 - show inline
                        // Append base64 image as markdown
                        const inlineImage = `\n\n![Generated Image](data:${event.mimeType || 'image/png'};base64,${event.data})`;
                        set((state) => ({
                            streamingContent: state.streamingContent + inlineImage,
                        }));
                    } else if (event.type === 'done') {
                        const { streamingContent } = get();
                        if (streamingContent) {
                            const assistantMessage: Message = {
                                id: Date.now() + 1,
                                role: 'assistant',
                                content: streamingContent,
                                type: 'text',
                                model: null,  // Will be set correctly after loadChat()
                                r2_path: null,
                                created_at: new Date().toISOString(),
                            };
                            set((state) => ({
                                messages: [...state.messages, assistantMessage],
                                isStreaming: false,
                                streamingContent: '',
                            }));
                        } else {
                            set({ isStreaming: false, streamingContent: '' });
                        }
                        // Reload messages from server to get correct r2_path for uploaded images
                        const chatId = get().currentChatId;
                        if (chatId) {
                            get().loadChat(chatId);
                        }
                    }
                }
            );
        } catch (error) {
            console.error('Failed to send message:', error);
            set({ isStreaming: false, streamingContent: '' });
        }
    },

    clearStreaming: () => {
        set({ isStreaming: false, streamingContent: '' });
    },

    deleteMessages: async (messageIds: number[]) => {
        const { currentChatId } = get();
        if (!currentChatId || messageIds.length === 0) return;

        try {
            const response = await chatApi.deleteMessages(currentChatId, messageIds);
            if (response.success) {
                set((state) => ({
                    messages: state.messages.filter(m => !messageIds.includes(m.id)),
                }));
            }
        } catch (error) {
            console.error('Failed to delete messages:', error);
        }
    },

    loadModels: async () => {
        try {
            const response = await geminiApi.getModels();
            if (response.success) {
                set({ availableModels: response.models });
                if (response.models.length > 0 && !get().selectedModel) {
                    set({ selectedModel: response.models[0].model_id });
                }
            }
        } catch (error) {
            console.error('Failed to load models:', error);
        }
    },

    setSelectedModel: (model: string) => {
        localStorage.setItem('selectedModel', model);
        set({ selectedModel: model });
        // Save model to current chat in DB
        const { currentChatId } = get();
        if (currentChatId) {
            chatApi.update(currentChatId, { model }).catch(err => {
                console.error('Failed to save model to chat:', err);
            });
        }
    },

    setSelectedPromptId: (id: number | null) => {
        set({ selectedPromptId: id });
    },

    addLocalMessage: (message: Omit<Message, 'id'>) => {
        set((state) => ({
            messages: [...state.messages, { ...message, id: Date.now() } as Message],
        }));
    },
}));
